import { useState } from 'react';
import changelogJson from '../generated/changelog.json';

interface ChangelogSection {
  title: string;
  items: string[];
}

interface ChangelogEntry {
  version: string;
  date: string | null;
  sections: ChangelogSection[];
}

const changelogData: ChangelogEntry[] = changelogJson;

const SECTION_COLORS: Record<string, string> = {
  'New Features': 'text-emerald-400',
  'Bugfixes': 'text-amber-400',
  'Changes': 'text-blue-400',
};

export function ChangelogButton() {
  const [open, setOpen] = useState(false);
  const currentVersion = changelogData[0]?.version ?? '1.0.0';

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-gray-500 hover:text-gray-300 transition-colors"
      >
        v{currentVersion}
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 char-grain">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative parchment-panel parchment-panel-solid rounded-card shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col text-left">
            <div className="flex items-center justify-between px-5 py-4 border-b border-ink-border/60">
              <h2 className="text-lg font-semibold text-gray-100">Changelog</h2>
              <button
                onClick={() => setOpen(false)}
                className="text-gray-500 hover:text-gray-300 transition-colors text-xl leading-none"
              >
                &times;
              </button>
            </div>

            <div className="overflow-y-auto px-5 py-4 space-y-6">
              {changelogData.length === 0 && (
                <p className="text-gray-500 text-sm">No changelog entries yet.</p>
              )}
              {changelogData.map((entry) => (
                <div key={entry.version}>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-base font-bold text-gray-100">v{entry.version}</span>
                    {entry.date && (
                      <span className="text-xs text-gray-500">{entry.date}</span>
                    )}
                  </div>

                  {entry.sections.length === 0 ? (
                    <p className="text-gray-500 text-sm italic">No notable changes.</p>
                  ) : (
                    <div className="space-y-2">
                      {entry.sections.map((section) => (
                        <div key={section.title}>
                          <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1 ${SECTION_COLORS[section.title] ?? 'text-gray-400'}`}>
                            {section.title}
                          </h3>
                          <ul className="space-y-0.5">
                            {section.items.map((item, i) => (
                              <li key={i} className="text-sm text-gray-300 flex gap-2">
                                <span className="text-gray-600 select-none">•</span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
